import { html, CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './outline-video-vimeo.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import Player from '@vimeo/player';

export interface OutlineVideoVimeoInterface extends HTMLElement {
  videoID: string;
}

/**
 * The Outline VideoVimeo component
 *
 * @element outline-video-vimeo
 */
@customElement('outline-video-vimeo')
export class OutlineVideoVimeo
  extends OutlineElement
  implements OutlineVideoVimeoInterface
{
  static styles: CSSResultGroup = [componentStyles];

  hash: string;
  url: string;
  title: string;
  duration: number;
  currentProgress: number;
  firedProgressEvent: { [key: string]: boolean } = {
    '10': false,
    '25': false,
    '50': false,
    '75': false,
    '90': false,
  };

  constructor() {
    super();
  }

  @property({ type: String, attribute: 'video-id', reflect: true })
  videoID: string;

  formatEventName(eventName: string) {
    if (eventName === 'ended') {
      return 'Complete';
    } else {
      return eventName.charAt(0).toUpperCase() + eventName.slice(1);
    }
  }

  getProgress(data: { percent: number }) {
    return Math.trunc(data.percent * 100);
  }

  needProgressEvent(data: { percent: number }) {
    const currentProgress = this.getProgress(data);
    const progressValueToSend: string | undefined = Object.keys(
      this.firedProgressEvent
    ).find(key => this.firedProgressEvent[key] === false);
    let progressValueToSendNum: number | null = null;

    if (!progressValueToSend) {
      return false;
    } else {
      progressValueToSendNum = parseInt(progressValueToSend);
    }

    if (currentProgress >= progressValueToSendNum) {
      this.firedProgressEvent[progressValueToSend] = true;
      return progressValueToSend;
    }

    return false;
  }

  fireEvent(eventName: string, data: { percent: number }) {
    // Catch to prevent pause event logging when video stops.
    if (eventName === 'pause' && data.percent === 1) {
      return;
    }

    const needToSendProgressValue =
      eventName === 'progress' ? this.needProgressEvent(data) : true;

    const dataLayerData = {
      videoTitle: this.title,
      videoSource: this.url,
      videoAction: this.formatEventName(eventName),
      videoDuration: this.duration,
      videoCurrentProgress: this.currentProgress,
      Progress:
        eventName === 'progress'
          ? needToSendProgressValue
          : this.getProgress(data),
    };

    if (eventName !== 'progress' || needToSendProgressValue) {
      window.dispatchEvent(
        new CustomEvent('vimeo-video-gtm-event', {
          detail: {
            dataLayerData,
          },
        })
      );
    }
  }

  firstUpdated() {
    this.url = `https://player.vimeo.com/video/${this.videoID}`;
    const iframe = this.shadowRoot?.querySelector('iframe');
    if (iframe) {
      const player = new Player(iframe);

      player.on('play', (data: { percent: number }) =>
        this.fireEvent('play', data)
      );
      player.on('pause', (data: { percent: number }) =>
        this.fireEvent('pause', data)
      );
      player.on('ended', (data: { percent: number }) =>
        this.fireEvent('ended', data)
      );
      player.on('seeked', (data: { percent: number }) =>
        this.fireEvent('seeked', data)
      );
      player.on('timeupdate', () =>
        player
          .getCurrentTime()
          .then((seconds: number) => (this.currentProgress = seconds))
      );

      player.getVideoTitle().then((title: string) => (this.title = title));
      player
        .getDuration()
        .then((duration: number) => (this.duration = duration));
    }
  }

  render() {
    return html` <div class="video">
      <iframe
        src="https://player.vimeo.com/video/${this.videoID}"
        width="480px"
        height="270px"
        frameborder="0"
        webkitallowfullscreen
        mozallowfullscreen
        allowfullscreen
      >
      </iframe>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-video-vimeo': OutlineVideoVimeo;
  }
}
